// src/pages/dashboardPage.js
import React from 'react';
import { Tabs, Badge } from 'antd';
import { DashboardOutlined, DollarOutlined, ShopOutlined, AppstoreOutlined, TrophyOutlined, LineChartOutlined, TagOutlined, RocketOutlined } from '@ant-design/icons';
const isDemoMode = process.env.REACT_APP_DEMO_MODE === 'true';
import { useNewFeatures } from '../hooks/useNewFeatures';
import BrandList from '../components/panel/brandslist';
import BrandsStatus from '../components/panel/BrandsStatus';
import GetDolar from '../components/panel/getdolar';
import UptimeRegistry from '../components/panel/uptime';
import DailySalesCard from '../components/panel/DailySalesCard';
import DashboardSummaryCards from '../components/panel/DashboardSummaryCards';
import WeekMonthSummary from '../components/panel/WeekMonthSummary';
import TopProductsToday from '../components/panel/TopProductsToday';
import SalesByCategory from '../components/panel/SalesByCategory';
import BestSellersCard from '../components/panel/BestSellersCard';
import InvoiceAnalytics from '../components/panel/InvoiceAnalytics';
import HistoricalTrends from '../components/panel/HistoricalTrends';
import PaymentMethodsChart from '../components/panel/PaymentMethodsChart';
import DesarrolloRoadmap from '../components/panel/DesarrolloRoadmap';
import ProfitAnalysis from '../components/analytics/ProfitAnalysis';
import MonthlyReportButton from '../components/panel/MonthlyReportButton';
import './dashboardPage.css';

function DashboardPage() {
    const { hasNewFeatures, newFeaturesCount } = useNewFeatures();

    const baseItems = [
        {
            key: '1',
            label: (
                <span>
                    <ShopOutlined />
                    Vista General
                </span>
            ),
            children: (
                <>
                    {/* Summary Cards at Top */}
                    <DashboardSummaryCards />
                    
                    {/* Ventas Hoy and Top Productos Hoy below */}
                    <div className="dashboard-row">
                        <div className="dashboard-col-5">
                            <DailySalesCard />
                        </div>
                        <div className="dashboard-col-7">
                            <TopProductsToday />
                        </div>
                    </div>
                </>
            ),
        },
        {
            key: '2',
            label: (
                <span>
                    <DollarOutlined />
                    Ventas
                </span>
            ),
            children: (
                <>
                    <div className="dashboard-row">
                        {/* Left column: Category (full height) */}
                        <div className="dashboard-col-6">
                            <SalesByCategory />
                        </div>
                        
                        {/* Right column: Period Summary + Payment Methods stacked */}
                        <div className="dashboard-col-6">
                            <div className="dashboard-cards-stack" style={{ gap: '8px' }}>
                                <WeekMonthSummary />
                                <PaymentMethodsChart />
                            </div>
                        </div>
                    </div>
                </>
            ),
        },
        {
            key: '3',
            label: (
                <span>
                    <TrophyOutlined />
                    Bestsellers
                </span>
            ),
            children: (
                <>
                    <div className="dashboard-row">
                        <div className="dashboard-col-12">
                            <BestSellersCard />
                        </div>
                    </div>
                </>
            ),
        },
        {
            key: '4',
            label: (
                <span>
                    <LineChartOutlined />
                    Análisis
                </span>
            ),
            children: (
                <Tabs
                    defaultActiveKey="4-1"
                    type="line"
                    size="middle"
                    tabBarExtraContent={<MonthlyReportButton />}
                    items={[
                        {
                            key: '4-1',
                            label: 'Análisis de Rentabilidad',
                            children: (
                                <div className="dashboard-row">
                                    <div className="dashboard-col-12">
                                        <ProfitAnalysis />
                                    </div>
                                </div>
                            ),
                        },
                        {
                            key: '4-2',
                            label: 'Tendencias',
                            children: (
                                <div className="dashboard-row">
                                    <div className="dashboard-col-12">
                                        <HistoricalTrends />
                                    </div>
                                </div>
                            ),
                        },
                        {
                            key: '4-3',
                            label: 'Análisis de Facturas',
                            children: (
                                <div className="dashboard-row">
                                    <div className="dashboard-col-12">
                                        <InvoiceAnalytics />
                                    </div>
                                </div>
                            ),
                        },
                    ]}
                />
            ),
        },
        {
            key: '5',
            label: (
                <span>
                    <TagOutlined />
                    Marcas
                </span>
            ),
            children: (
                <>
                    {/* Marcas status tables */}
                    <div className="dashboard-row" style={{ overflowX: 'hidden' }}>
                        <div className="dashboard-col-12" style={{ overflowX: 'hidden' }}>
                            <BrandsStatus />
                        </div>
                    </div>
                </>
            ),
        },
        {
            key: '6',
            label: (
                <span style={{ position: 'relative', display: 'inline-block' }}>
                    <RocketOutlined />
                    Desarrollo
                    {hasNewFeatures && (
                        <sup style={{ 
                            position: 'absolute', 
                            top: -10, 
                            right: -18,
                            backgroundColor: '#52c41a',
                            color: '#fff',
                            borderRadius: '10px',
                            padding: '0 6px',
                            fontSize: '12px',
                            fontWeight: 'bold',
                            lineHeight: '18px',
                            minWidth: '18px',
                            textAlign: 'center'
                        }}>
                            {newFeaturesCount}
                        </sup>
                    )}
                </span>
            ),
            children: (
                <div className="scrollable-tab-content">
                    <div className="dashboard-row">
                        <div className="dashboard-col-12">
                            <DesarrolloRoadmap />
                        </div>
                    </div>
                </div>
            ),
        },
        {
            key: '7',
            label: (
                <span>
                    <AppstoreOutlined />
                    Todo
                </span>
            ),
            children: (
                <div className="scrollable-tab-content">
                    {/* All content in one view */}
                    <div className="dashboard-row">
                        <div className="dashboard-col-4">
                            <DailySalesCard />
                        </div>
                        <div className="dashboard-col-4">
                            <GetDolar />
                        </div>
                        <div className="dashboard-col-4">
                            <UptimeRegistry />
                        </div>
                    </div>

                    <div className="dashboard-row">
                        <div className="dashboard-col-12">
                            <TopProductsToday />
                        </div>
                    </div>

                    <div className="dashboard-row">
                        <div className="dashboard-col-12">
                            <BestSellersCard />
                        </div>
                    </div>

                    <div className="dashboard-row">
                        <div className="dashboard-col-6">
                            <InvoiceAnalytics />
                        </div>
                        <div className="dashboard-col-6">
                            <HistoricalTrends />
                        </div>
                    </div>

                    <div className="dashboard-row">
                        <div className="dashboard-col-12">
                            <ProfitAnalysis />
                        </div>
                    </div>

                    <div className="dashboard-row">
                        <div className="dashboard-col-6">
                            <SalesByCategory />
                        </div>
                        <div className="dashboard-col-6">
                            <WeekMonthSummary />
                        </div>
                    </div>

                    <div className="dashboard-row">
                        <div className="dashboard-col-12">
                            <PaymentMethodsChart />
                        </div>
                    </div>

                    <div className="dashboard-row">
                        <div className="dashboard-col-12">
                            <BrandList />
                        </div>
                    </div>
                </div>
            ),
        },
    ];

    const items = isDemoMode ? baseItems.filter(item => item.key !== '6') : baseItems;

    return (
        <div className="dashboard-container">
            <Tabs 
                defaultActiveKey="1" 
                items={items} 
                size="large"
                type="card"
                className="dashboard-tabs"
            />
        </div>
    );
}

export default DashboardPage;
